import React, { useEffect, useMemo, useState } from 'react';
import { PlayCircle, Clock, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { getAllTutorialVideos, getDefaultVideoForRole, type TutorialVideo } from '../data/tutorialVideos';

const AUDIENCE_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  helpAudienceAll: { bg: '#ede9fe', text: '#6d28d9' },
  helpAudienceAdmin: { bg: '#fee2e2', text: '#b91c1c' },
  helpAudienceEmployee: { bg: '#dbeafe', text: '#1d4ed8' },
  helpAudienceClient: { bg: '#d1fae5', text: '#047857' },
};

const Ajuda: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const isStackedLayout = useMediaQuery('(max-width: 1023px)');
  const videos = useMemo(() => getAllTutorialVideos(), []);
  const [selectedVideo, setSelectedVideo] = useState<TutorialVideo>(() =>
    getDefaultVideoForRole(user?.role),
  );

  useEffect(() => {
    const defaultVideo = getDefaultVideoForRole(user?.role);
    setSelectedVideo(defaultVideo);
  }, [user?.role]);

  const handleSelectVideo = (video: TutorialVideo) => {
    setSelectedVideo(video);
  };

  return (
    <div style={{ padding: isStackedLayout ? '16px' : '30px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <BookOpen size={28} color="#9333ea" />
          <h1 style={{ margin: 0, color: '#333', fontSize: '28px' }}>{t('helpTitle')}</h1>
        </div>
        <p style={{ margin: 0, color: '#666', fontSize: '15px', lineHeight: 1.6 }}>
          {t('helpSubtitle')}
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isStackedLayout ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 320px',
          gap: '24px',
          alignItems: 'start',
        }}
        data-testid="help-page"
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <video
            key={selectedVideo.id}
            src={selectedVideo.src}
            controls
            playsInline
            preload="metadata"
            data-testid={`help-video-${selectedVideo.id}`}
            style={{
              width: '100%',
              display: 'block',
              backgroundColor: '#111',
              aspectRatio: '16 / 9',
            }}
          />
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#333', fontSize: '20px' }}>
                {t(selectedVideo.titleKey)}
              </h2>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: '9999px',
                  backgroundColor: (AUDIENCE_BADGE_COLORS[selectedVideo.audienceLabelKey] ?? AUDIENCE_BADGE_COLORS.helpAudienceAll).bg,
                  color: (AUDIENCE_BADGE_COLORS[selectedVideo.audienceLabelKey] ?? AUDIENCE_BADGE_COLORS.helpAudienceAll).text,
                  textTransform: 'uppercase',
                }}
              >
                {t(selectedVideo.audienceLabelKey)}
              </span>
            </div>
            <p style={{ margin: 0, color: '#666', fontSize: '14px', lineHeight: 1.6 }}>
              {t(selectedVideo.descriptionKey)}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h3 style={{ margin: '0 0 4px 0', color: '#333', fontSize: '16px' }}>
            {t('helpVideoList')}
          </h3>
          {videos.map(video => {
            const isActive = selectedVideo.id === video.id;
            const badge = AUDIENCE_BADGE_COLORS[video.audienceLabelKey] ?? AUDIENCE_BADGE_COLORS.helpAudienceAll;
            const isRecommended = video.audience === user?.role || (video.audience === 'all' && !user?.role);
            return (
              <button
                key={video.id}
                type="button"
                onClick={() => handleSelectVideo(video)}
                data-testid={`help-video-card-${video.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '14px 16px',
                  border: isActive ? '2px solid #9333ea' : '1px solid #e5e7eb',
                  borderRadius: '10px',
                  backgroundColor: isActive ? '#faf5ff' : 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  boxShadow: isActive ? '0 2px 8px rgba(147,51,234,0.12)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <PlayCircle
                  size={22}
                  color={isActive ? '#9333ea' : '#6b7280'}
                  style={{ flexShrink: 0, marginTop: '2px' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span
                      style={{
                        fontWeight: 600,
                        color: isActive ? '#7e22ce' : '#374151',
                        fontSize: '14px',
                      }}
                    >
                      {t(video.titleKey)}
                    </span>
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        backgroundColor: badge.bg,
                        color: badge.text,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                      }}
                    >
                      {t(video.audienceLabelKey)}
                    </span>
                    {isRecommended && user?.role && (video.audience === user.role || (user.role === 'supervisor' && video.id === 'admin-full')) && (
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                        }}
                      >
                        {t('helpRecommended')}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      lineHeight: 1.4,
                      marginBottom: '6px',
                    }}
                  >
                    {t(video.descriptionKey)}
                  </div>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      color: '#9ca3af',
                    }}
                  >
                    <Clock size={12} />
                    {video.durationMinutes} min
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          marginTop: '24px',
          padding: '16px 20px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '10px',
          color: '#0c4a6e',
          fontSize: '14px',
          lineHeight: 1.5,
        }}
      >
        {t('helpTip')}
      </div>
    </div>
  );
};

export default Ajuda;
